/**
 * Operating Agreement - Manager Managed
 * Business Entity (LLC) specific document
 * Full authoritative version for LLC governance
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateOperatingAgreementHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body {
      font-family: 'Cambria', 'Georgia', serif;
      font-size: 11pt;
      line-height: 1.6;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 1in;
    }
    h1 {
      text-align: center;
      font-size: 16pt;
      margin-bottom: 0.3in;
      font-weight: bold;
      text-transform: uppercase;
    }
    h2 {
      font-size: 12pt;
      margin-top: 0.3in;
      margin-bottom: 0.1in;
      font-weight: bold;
    }
    .article {
      margin-bottom: 0.25in;
    }
    .section {
      margin-bottom: 0.2in;
      margin-left: 0.25in;
    }
    .subsection {
      margin-left: 0.5in;
      margin-bottom: 0.1in;
    }
    .signature-section {
      margin-top: 0.5in;
      page-break-inside: avoid;
    }
    .signature-block {
      margin-top: 0.4in;
    }
    .signature-line {
      border-bottom: 1px solid #000;
      width: 3.5in;
      margin-top: 0.3in;
      margin-bottom: 0.1in;
    }
  </style>
</head>
<body>
  <h1>Operating Agreement<br>{{entity_legal_name}}</h1>
  
  <div style="text-align: center; margin-bottom: 0.5in;">
    Manager-Managed Limited Liability Company<br>
    Formed under the Laws of {{entity_state_of_formation}}
  </div>
  
  <div class="article">
    This Operating Agreement (this "Agreement") of {{entity_legal_name}} (the "Company") 
    is entered into effective {{contribution_date}}, by and among the Company and its 
    Member, as follows:
  </div>
  
  <h2>ARTICLE I. ORGANIZATION</h2>
  
  <div class="section">
    <strong>1.1 Formation.</strong> The Company was formed as a {{entity_type}} under 
    the laws of {{entity_state_of_formation}} on {{entity_formation_date}}.
  </div>
  
  <div class="section">
    <strong>1.2 Name.</strong> The name of the Company is {{entity_legal_name}}.
  </div>
  
  <div class="section">
    <strong>1.3 Purpose.</strong> The purpose of the Company is: {{entity_business_purpose}}.
  </div>
  
  <div class="section">
    <strong>1.4 Principal Office.</strong> The principal office of the Company shall be 
    determined by the Manager and may be changed from time to time.
  </div>
  
  <h2>ARTICLE II. MEMBERS</h2>
  
  <div class="section">
    <strong>2.1 Member.</strong> The sole member of the Company is:<br>
    {{receiving_entity}}<br>
    An entity under the stewardship of {{una_name}}
  </div>
  
  <div class="section">
    <strong>2.2 Member Interest.</strong> The Member holds 100% of the membership interest 
    in the Company.
  </div>
  
  <div class="section">
    <strong>2.3 Member Rights.</strong> The Member shall have all rights and privileges 
    afforded to members under the laws of {{entity_state_of_formation}}, including the 
    right to vote on major Company decisions and the right to receive distributions.
  </div>
  
  <h2>ARTICLE III. MANAGEMENT</h2>
  
  <div class="section">
    <strong>3.1 Manager-Managed.</strong> The Company shall be managed by a Manager, 
    not by its Member. The Member delegates full management authority to the Manager.
  </div>
  
  <div class="section">
    <strong>3.2 Appointment of Manager.</strong> The Manager of the Company is:<br>
    {{manager_name}}, {{manager_title}}<br>
    Appointed by {{una_name}} through its governance process
  </div>
  
  <div class="section">
    <strong>3.3 Manager Authority.</strong> The Manager shall have complete authority 
    to manage and control the business and affairs of the Company, including but not 
    limited to:
    <div class="subsection">
      (a) Entering into contracts and agreements on behalf of the Company;
    </div>
    <div class="subsection">
      (b) Opening and managing bank accounts;
    </div>
    <div class="subsection">
      (c) Hiring and terminating employees and contractors;
    </div>
    <div class="subsection">
      (d) Making ordinary business decisions;
    </div>
    <div class="subsection">
      (e) Filing tax returns and maintaining Company records.
    </div>
  </div>
  
  <div class="section">
    <strong>3.4 Major Decisions.</strong> The following decisions require Member approval:
    <div class="subsection">
      (a) Dissolution of the Company;
    </div>
    <div class="subsection">
      (b) Amendment of this Operating Agreement;
    </div>
    <div class="subsection">
      (c) Sale of substantially all Company assets;
    </div>
    <div class="subsection">
      (d) Merger or conversion of the Company.
    </div>
  </div>
  
  <div class="section">
    <strong>3.5 Compensation.</strong> The Manager may receive reasonable compensation 
    for services rendered to the Company, as determined by the Member.
  </div>
  
  <h2>ARTICLE IV. CAPITAL CONTRIBUTIONS AND DISTRIBUTIONS</h2>
  
  <div class="section">
    <strong>4.1 Capital Contributions.</strong> The Member has made capital contributions 
    to the Company as evidenced by Company records.
  </div>
  
  <div class="section">
    <strong>4.2 Distributions.</strong> Distributions shall be made to the Member at such 
    times and in such amounts as determined by the Manager, subject to applicable law and 
    the financial needs of the Company.
  </div>
  
  <h2>ARTICLE V. BOOKS AND RECORDS</h2>
  
  <div class="section">
    <strong>5.1 Records.</strong> The Manager shall maintain complete and accurate books 
    and records of the Company, including financial statements, tax returns, and this 
    Operating Agreement.
  </div>
  
  <div class="section">
    <strong>5.2 Fiscal Year.</strong> The fiscal year of the Company shall be the calendar year.
  </div>
  
  <h2>ARTICLE VI. ECCLESIASTICAL STEWARDSHIP</h2>
  
  <div class="section">
    <strong>6.1 Sacred Purpose.</strong> The parties acknowledge that the Company is held 
    under the stewardship of {{una_name}} and shall be operated in accordance with the 
    principles and purposes of the UNA.
  </div>
  
  <div class="section">
    <strong>6.2 Member Accountability.</strong> The Member, through {{una_name}}, maintains 
    ultimate accountability for ensuring the Company operates in alignment with sacred 
    purposes and ecclesiastical principles.
  </div>
  
  <h2>ARTICLE VII. MISCELLANEOUS</h2>
  
  <div class="section">
    <strong>7.1 Amendments.</strong> This Agreement may be amended only with the written 
    consent of the Member.
  </div>
  
  <div class="section">
    <strong>7.2 Governing Law.</strong> This Agreement shall be governed by the laws of 
    {{entity_state_of_formation}}.
  </div>
  
  <div class="section">
    <strong>7.3 Binding Effect.</strong> This Agreement shall be binding upon and inure 
    to the benefit of the parties and their respective successors and assigns.
  </div>
  
  <div class="signature-section">
    <div style="text-align: center; font-weight: bold; margin-bottom: 0.3in;">
      IN WITNESS WHEREOF, the Member has executed this Operating Agreement as of 
      {{contribution_date}}.
    </div>
    
    <div class="signature-block">
      <div style="font-weight: bold; margin-bottom: 0.2in;">MEMBER:</div>
      <div>{{receiving_entity}}</div>
      <div class="signature-line"></div>
      <div style="font-size: 10pt;">
        By: {{treasury_secretary_name}}, {{primary_officer_role}}<br>
        On behalf of {{una_name}}
      </div>
    </div>
    
    <div class="signature-block">
      <div style="font-weight: bold; margin-bottom: 0.2in;">MANAGER ACKNOWLEDGMENT:</div>
      <div class="signature-line"></div>
      <div style="font-size: 10pt;">
        {{manager_name}}, {{manager_title}}<br>
        I acknowledge my appointment and accept the responsibilities of Manager
      </div>
    </div>
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}

/**
 * Operating Agreement - Short Form
 * Simplified version for bank presentation
 */
export function generateOperatingAgreementShortFormHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body {
      font-family: 'Cambria', 'Georgia', serif;
      font-size: 12pt;
      line-height: 1.8;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 1in;
    }
    h1 {
      text-align: center;
      font-size: 16pt;
      margin-bottom: 0.5in;
      font-weight: bold;
    }
    .section {
      margin-bottom: 0.3in;
    }
  </style>
</head>
<body>
  <h1>Operating Agreement - Short Form<br>{{entity_legal_name}}</h1>
  
  <div style="text-align: center; margin-bottom: 0.5in; font-size: 11pt;">
    For Banking and Financial Institution Purposes
  </div>
  
  <div class="section">
    <strong>Company Name:</strong> {{entity_legal_name}}
  </div>
  
  <div class="section">
    <strong>Formation:</strong> {{entity_type}} formed under the laws of {{entity_state_of_formation}} 
    on {{entity_formation_date}}
  </div>
  
  <div class="section">
    <strong>Management Type:</strong> Manager-Managed (not Member-Managed)
  </div>
  
  <div class="section">
    <strong>Member:</strong> {{receiving_entity}}<br>
    An entity under the stewardship of {{una_name}}
  </div>
  
  <div class="section">
    <strong>Manager:</strong> {{manager_name}}, {{manager_title}}
  </div>
  
  <div class="section">
    <strong>Manager Authority:</strong> The Manager has full authority to:
    <ul>
      <li>Open and manage bank accounts</li>
      <li>Enter into contracts and agreements</li>
      <li>Sign checks and financial documents</li>
      <li>Make ordinary business decisions</li>
      <li>Conduct all day-to-day business operations</li>
    </ul>
  </div>
  
  <div class="section">
    <strong>Certification:</strong> This Short Form Operating Agreement accurately reflects 
    the governance structure of {{entity_legal_name}} as set forth in the Company's full 
    Operating Agreement executed {{contribution_date}}.
  </div>
  
  <div style="margin-top: 0.75in; page-break-inside: avoid;">
    <div style="border-bottom: 1px solid #000; width: 3.5in; margin-top: 0.4in;"></div>
    <div style="font-size: 10pt; margin-top: 0.1in;">
      {{treasury_secretary_name}}, {{primary_officer_role}}<br>
      On behalf of {{receiving_entity}}
    </div>
    <div style="margin-top: 0.3in;">
      Date: ___________________
    </div>
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}
